import { useState, type FormEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { BucketColorPicker } from './BucketColorPicker'
import { BucketIconPicker } from './BucketIconPicker'
import { useCreateBucket, useUpdateBucket } from '@/hooks/useBuckets'
import { BUCKET_PALETTE } from '@/lib/colors'
import type { Bucket } from '@/types'

interface BucketFormProps {
  bucket?: Bucket
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BucketForm({ bucket, open, onOpenChange }: BucketFormProps) {
  const isEditing = !!bucket

  const [name, setName] = useState(bucket?.name ?? '')
  const [color, setColor] = useState(bucket?.color ?? BUCKET_PALETTE[0])
  const [icon, setIcon] = useState<string | undefined>(bucket?.icon)
  const [intention, setIntention] = useState(bucket?.intention ?? '')
  const [visionStatement, setVisionStatement] = useState(bucket?.visionStatement ?? '')
  const [targetAllocPct, setTargetAllocPct] = useState(
    bucket?.targetAlloc != null ? String(Math.round(bucket.targetAlloc * 100)) : '',
  )
  const [saving, setSaving] = useState(false)
  const createBucket = useCreateBucket()
  const updateBucket = useUpdateBucket()

  // Reset form when dialog opens
  function handleOpenChange(next: boolean) {
    if (next) {
      setName(bucket?.name ?? '')
      setColor(bucket?.color ?? BUCKET_PALETTE[0])
      setIcon(bucket?.icon)
      setIntention(bucket?.intention ?? '')
      setVisionStatement(bucket?.visionStatement ?? '')
      setTargetAllocPct(
        bucket?.targetAlloc != null ? String(Math.round(bucket.targetAlloc * 100)) : '',
      )
    }
    onOpenChange(next)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) return

    const pct = targetAllocPct.trim()
    const targetAlloc =
      pct !== '' && !isNaN(Number(pct)) ? Math.min(100, Math.max(0, Number(pct))) / 100 : undefined

    setSaving(true)
    try {
      if (isEditing) {
        await updateBucket.mutateAsync({
          id: bucket.id,
          data: {
            name: trimmedName,
            color,
            icon: icon || undefined,
            intention: intention.trim() || undefined,
            visionStatement: visionStatement.trim() || undefined,
            targetAlloc,
          },
        })
      } else {
        await createBucket.mutateAsync({
          name: trimmedName,
          color,
          icon: icon || undefined,
          intention: intention.trim() || undefined,
          visionStatement: visionStatement.trim() || undefined,
          targetAlloc,
        })
      }
      onOpenChange(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit area' : 'New area of life'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="bucket-name">Name</Label>
            <div className="flex items-center gap-2">
              <BucketColorPicker color={color} onChange={setColor} />
              <BucketIconPicker icon={icon} color={color} onChange={setIcon} />
              <Input
                id="bucket-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Faith, Family, Vocation"
                autoFocus
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bucket-intention">
              Intention{' '}
              <span className="text-muted-foreground font-normal text-xs">— optional</span>
            </Label>
            <Input
              id="bucket-intention"
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              placeholder="What does faithfulness here look like?"
              maxLength={200}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bucket-vision">
              Vision{' '}
              <span className="text-muted-foreground font-normal text-xs">— optional</span>
            </Label>
            <Textarea
              id="bucket-vision"
              value={visionStatement}
              onChange={(e) => setVisionStatement(e.target.value)}
              placeholder="What does thriving look like in this area 5 years from now?"
              rows={2}
              className="resize-none text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bucket-alloc">
              Target allocation %{' '}
              <span className="text-muted-foreground font-normal text-xs">— optional</span>
            </Label>
            <Input
              id="bucket-alloc"
              type="number"
              min={0}
              max={100}
              step={1}
              value={targetAllocPct}
              onChange={(e) => setTargetAllocPct(e.target.value)}
              placeholder="e.g. 20"
              className="w-28"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || saving}>
              {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
